import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { CreateUpdatePartTagTemplateRequest, IPartTagTemplate } from "../models/PartTagTemplate";
import { partLabelDisplayQueryKeyPrefix } from "./usePartLabels";

export const partTagTemplatesQueryKey = ["partTagTemplates"] as const;

export default function usePartTagTemplates() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();
  const staleTime = 1000 * 60 * 10;

  const tags = useQuery({
    queryKey: partTagTemplatesQueryKey,
    queryFn: () => get<DefaultResponseWithData<IPartTagTemplate[]>>("/parttagtemplate"),
    staleTime,
  });

  // Printing caches the resolved label display for an hour, so every edit has to drop that
  // cache too or the next print would still use the old layout.
  const invalidate = () => {
    queryClient.invalidateQueries(partTagTemplatesQueryKey);
    queryClient.invalidateQueries(partLabelDisplayQueryKeyPrefix);
  };

  const create = useMutation(
    (req: CreateUpdatePartTagTemplateRequest) =>
      post<CreateUpdatePartTagTemplateRequest, DefaultResponseWithData<IPartTagTemplate>>(`/parttagtemplate`, req),
    { onSuccess: invalidate }
  );

  const update = useMutation(
    (params: { id: string; req: CreateUpdatePartTagTemplateRequest }) =>
      put<CreateUpdatePartTagTemplateRequest, DefaultResponseWithData<IPartTagTemplate>>(`/parttagtemplate/${params.id}`, params.req),
    { onSuccess: invalidate }
  );

  const remove = useMutation(
    (id: string) => del(`/parttagtemplate/${id}`),
    { onSuccess: invalidate }
  );

  // Re-applies the legacy Tags:* settings to the Default tag.
  const resetDefault = useMutation(
    () => post<undefined, DefaultResponseWithData<IPartTagTemplate>>(`/parttagtemplate/default/reset`, undefined),
    { onSuccess: invalidate }
  );

  const setCategories = useMutation(
    (params: { id: string; categoryIds: string[] }) =>
      put<string[], DefaultResponseWithData<IPartTagTemplate>>(`/parttagtemplate/${params.id}/categories`, params.categoryIds),
    { onSuccess: invalidate }
  );

  return { tags, create, update, remove, setCategories, resetDefault };
}
